import { useEffect, useState } from 'react';
import { Box } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import RefreshIcon from '@material-ui/icons/RefreshOutlined';
import { Alert, AlertTitle } from '@material-ui/lab';
import { Layout } from '@roc/client-portal-shared/components';
import { observer } from 'mobx-react';
import {
  LoanTabs,
  MyLoansCards,
  MyLoansGrid,
  MyPipelineLoansGrid,
  useLoanStore,
  MyRequestLoansGrid,
  MyAgreedLoansGrid,
  MyQuoteLoansGrid,
  MyTasksGridExternal,
  MyManualQuoteLoansGrid
} from '@roc/feature-loans';
import {
  myPipelineLoansColumns,
  myRequestLoansColumns,
  myPendingApprovalLoansColumns,
  myAgreedLoansColumns,
  myQuoteLoansColumns
} from './loanColumns';
import { UnreadMessages } from './unreadMessages';
import { useBaseStore, useUserStore } from '@roc/feature-app-core';
import { insertIf } from '@roc/feature-utils';
import { makeStyles } from '@material-ui/core/styles';


const LOANS_TAB = {
  MY_TASKS: 'MY TASKS',
  PIPELINE: 'PIPELINE',
  AGREED_LOANS: 'AGREED LOANS',
  MY_LOANS: 'MY LOANS',
  LOAN_QUOTES: 'LOAN QUOTES',
  LOAN_REQUESTS: 'LOAN REQUESTS',
  BACKUP_LOANS: 'BACKUP LOANS',
  MANUAL_QUOTE: 'MANUAL QUOTE',
}

const useStyles = makeStyles({
  buttonIcon: {
    fontSize: 'inherit',
    marginRight: 0,
  },
});

const renderTabContent = (globalStore, loanStore, TABS, totalLoanRequestCount) => {
  const { userStore } = useUserStore();
  const tabName = TABS[loanStore.selectedTabIndex].label;
  switch (tabName) {
    case LOANS_TAB.PIPELINE:
      return globalStore.isListView ? (
        <MyPipelineLoansGrid columns={myPipelineLoansColumns} />
      ) : null;
    case LOANS_TAB.AGREED_LOANS:
      return globalStore.isListView ? (
        <MyAgreedLoansGrid columns={myAgreedLoansColumns} />
      ) : null;
    case LOANS_TAB.MY_LOANS:
      return globalStore.isListView ? <MyLoansGrid /> : <MyLoansCards />;
    case LOANS_TAB.MY_TASKS:
      return globalStore.isListView ? (
        <MyTasksGridExternal />
      ) : null;
    case LOANS_TAB.LOAN_QUOTES:
      return globalStore.isListView ? (
        <MyQuoteLoansGrid columns={myQuoteLoansColumns} />
      ) : null;
    case LOANS_TAB.LOAN_REQUESTS:
      return globalStore.isListView ? (
        <MyRequestLoansGrid
          columns={userStore.allowLoanPricing ? myRequestLoansColumns : myPendingApprovalLoansColumns}
          onLoanRequestChanged={totalLoanRequestCount}
        />
      ) : null;
    case LOANS_TAB.MANUAL_QUOTE:
      return globalStore.isListView ? (
        <MyManualQuoteLoansGrid />
      ) : null;
    case LOANS_TAB.BACKUP_LOANS:
      return globalStore.isListView ? <MyLoansGrid isForBackUpLoans={true} /> : <MyLoansCards isForBackUpLoans={true} />;
    default:
      console.error('Invalid tab index');
      break;
  }
};

export const LoansHome = observer(() => {
  const { globalStore } = useBaseStore();
  const { loanStore } = useLoanStore();
  const { userStore } = useUserStore();
  const { selectedTabIndex } = loanStore;
  const [totalCountLoanRequests, setTotalCountLoanRequests] = useState(0);
  const [totalCountManualQuotesOnSales, setTotalCountManualQuotesOnSales] = useState(0);
  const classes = useStyles();

  const PRIMARY_TABS = [
    ...insertIf(globalStore.userFeatures?.showMyTasksFirst, [
      {
        label: LOANS_TAB.MY_TASKS,
      },
    ]),
    {
      label: LOANS_TAB.PIPELINE,
    },
    {
      label: LOANS_TAB.AGREED_LOANS,
    },
    {
      label: LOANS_TAB.MY_LOANS,
    },
    ...insertIf(!globalStore.userFeatures?.showMyTasksFirst, [
      {
        label: LOANS_TAB.MY_TASKS,
      },
    ]),
    {
      label: LOANS_TAB.LOAN_QUOTES,
    },
    {
      label: LOANS_TAB.MANUAL_QUOTE,
      showBadge: totalCountManualQuotesOnSales > 0 ? totalCountManualQuotesOnSales : undefined,
    },
    ...insertIf(!globalStore.userFeatures?.isLenderOriginator || !userStore.allowLoanPricing, [
      {
        label: LOANS_TAB.LOAN_REQUESTS,
        showBadge: !globalStore.userFeatures?.isLenderOriginator && totalCountLoanRequests > 0 ? totalCountLoanRequests : undefined,
      },
    ]),
    ...insertIf(globalStore.userFeatures?.showBackUpLoans, [
      {
        label: LOANS_TAB.BACKUP_LOANS
      },
    ]),
  ];

  useEffect(() => {
    const isComingBackFromLoanDetails = globalStore.lastHistoryAction === 'POP';
    if (!isComingBackFromLoanDetails) {
      loanStore.setSelectedTabIndex(0);
    }
    totalLoanRequestCount();
    totalManualQuotesOnSalesCount();
  }, []);

  useEffect(() => {
    if (LOANS_TAB.BACKUP_LOANS === PRIMARY_TABS[selectedTabIndex]?.label) {
      fetchToggleState();
      fetchIsBackgroundTaskBeingProcessed();
    }
  }, [selectedTabIndex])

  const showSwitchView = () => {
    switch (selectedTabIndex) {
      case 1:
        return true;
      default:
        return false;
    }
  };

  const totalLoanRequestCount = async () => {
    const response = await loanStore.getLoanRequestsTotalCount();
    let totalCount = Number(response);
    totalCount = totalCount ? totalCount : 0;
    setTotalCountLoanRequests(totalCount);
  }

  const totalManualQuotesOnSalesCount = async () => {
    const response = await loanStore.getManualQuotesOnSalesTotalCount();
    let totalCount = Number(response);
    totalCount = totalCount ? totalCount : 0;
    setTotalCountManualQuotesOnSales(totalCount);
  }

  const fetchToggleState = async () => {
    loanStore.fetchConversationsToggleStatus(userStore?.userInformation?.userId);
  };

  const fetchIsBackgroundTaskBeingProcessed = async () => {
    loanStore.fetchIsBackgroundTaskBeingProcessed(userStore?.userInformation?.userId);
  }

  const handleConversationsButtonChange = async () => {
    loanStore.updateUserBackUpConversationToggle(userStore?.userInformation?.userId);
    loanStore.setConversationsToggle(!loanStore.conversationsToggle);
    loanStore.setIsBackgroundTaskBeingProcessed(!loanStore.isBackgroundTaskBeingProcessed);
  };

  const refresh = async () => {
    fetchIsBackgroundTaskBeingProcessed();
    loanStore.myBackUpLoansGridStore.refresh();
  };

  const showBackUpConversationsButton = loanStore.selectedTabIndex === 5 && loanStore.myBackUpLoansGridStore.gridData.data.rows.length > 0;
  const showUnreadMessages = loanStore.numberUnreadMessages > 0 && loanStore.selectedTabIndex === 0 && userStore.allowLoanPricing;

  return (
    <Layout>
      {/* plain flex boxes (not Grid): Grid's negative margins / unbounded item width caused a page-wide horizontal scroll */}
      <Box display="flex" flexDirection="column" flexGrow={1} minWidth={0} style={{ gap: 16 }}>
        {showUnreadMessages && (
          <Box>
            <UnreadMessages />
          </Box>
        )}
        <Box display="flex" flexDirection="column" flexGrow={1} minWidth={0}>
          <LoanTabs
            tabs={PRIMARY_TABS}
            showSwitchView={showSwitchView}
            totalLoanRequests={totalCountLoanRequests}
          />
          {showBackUpConversationsButton &&
            loanStore.conversationsToggle &&
            !loanStore.isBackgroundTaskBeingProcessed && (
              <Alert severity="info">
                <AlertTitle>
                  <b>
                    You have currently turned ON the setting to join all backup
                    loans conversations.
                  </b>
                </AlertTitle>
                <Box>
                  <Button
                    color="primary"
                    size="small"
                    variant="outlined"
                    onClick={() => handleConversationsButtonChange()}
                  >
                    Remove me from all backup conversations
                  </Button>
                </Box>
              </Alert>
            )}
          {showBackUpConversationsButton &&
            !loanStore.conversationsToggle &&
            !loanStore.isBackgroundTaskBeingProcessed && (
              <Alert severity="info">
                <AlertTitle>
                  <b>
                    You have currently turned OFF the setting to join all backup
                    loans conversations.
                  </b>
                </AlertTitle>
                <Box>
                  <Button
                    color="primary"
                    size="small"
                    variant="outlined"
                    onClick={() => handleConversationsButtonChange()}
                  >
                    Add me to all backup conversations
                  </Button>
                </Box>
              </Alert>
            )}
          {showBackUpConversationsButton &&
            loanStore.isBackgroundTaskBeingProcessed &&
            loanStore.conversationsToggle && (
              <Alert severity="warning">
                <AlertTitle>
                  <b>
                    Your request to join all backup conversations is currently
                    being processed.
                  </b>
                </AlertTitle>
                <p>
                  This may take few mins to complete. Click below to check the
                  latest status of your request.
                </p>
                <Box mt={1}>
                  <Button
                    color="primary"
                    size="small"
                    variant="outlined"
                    startIcon={<RefreshIcon className={classes.buttonIcon} />}
                    onClick={() => refresh()}
                  >
                    Check Status
                  </Button>
                </Box>
              </Alert>
            )}
          {showBackUpConversationsButton &&
            loanStore.isBackgroundTaskBeingProcessed &&
            !loanStore.conversationsToggle && (
              <Alert severity="warning">
                <AlertTitle>
                  <b>
                    Your request to leave all backup conversations is currently
                    being processed.
                  </b>
                </AlertTitle>
                <p>
                  This may take few mins to complete. Click below to check the
                  latest status of your request.
                </p>
                <Box mt={1}>
                  <Button
                    color="primary"
                    size="small"
                    variant="outlined"
                    startIcon={<RefreshIcon className={classes.buttonIcon} />}
                    onClick={() => refresh()}
                  >
                    Check Status
                  </Button>
                </Box>
              </Alert>
            )}
          {renderTabContent(globalStore, loanStore, PRIMARY_TABS, totalLoanRequestCount)}
        </Box>
      </Box>
    </Layout>
  );
});
